import { AxiosError } from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import {
  ErrorInfo,
  ScheduleListDownloadList,
  ScheduleListDownloadListItem,
  ScheduleListItem,
  ScheduleListPeriod,
  ScheduleListSearchConditions,
} from '../../../api/model';
import {
  useGetScheduleListMasters,
  usePostScheduleListDownload,
  usePostScheduleListSearch,
} from '../../../api/schedule-list/schedule-list';
import { useDefaultOptions, useOptionsForBinary } from '../../../hooks/api';
import { executeDownload } from '../../../utils/download';
import * as sessionStrageUtils from '../../../utils/sessionStorage';
import ErrorComponent from '../../atoms/ErrorComponent';
import NoDataComponent from '../../atoms/NoDataComponent';
import WaitComponent from '../../atoms/WaitComponent';
import BlobErrorModal from '../../modules/BlobErrorModal';
import ErrorModal from '../../modules/ErrorModal';
import SpinnerModal from '../../modules/SpinnerModal';
import GanttChartArea from './GanttChart/GanttChartArea';
import ScheduleListIndexArea from './ScheduleListIndexArea';
import ScheduleListSerchCondition from './ScheduleListSearchCondition';
import { N_DATA_PER_PAGE, ScheduleSortKey } from './SchedulelistCommon';

/**
 * タブタイトル
 * @returns
 */
function Title() {
  // 言語情報
  const { t } = useTranslation();
  return (
    <Helmet>
      <title>{t('CMS-POC|日程一覧')}</title>
    </Helmet>
  );
}

/**
 * 日程一覧画面
 */
function ScheduleListPage() {
  // 言語情報
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // 検索条件部分が開いているかどうか(開く)
  const [openSearchConditions, setOpenSearchConditions] = useState(true);

  // ガントチャート/詳細表示が表示されているかどうか(閉じている)
  const openGanttChartArea = useRef(sessionStrageUtils.get<boolean>('ScheduleList.OpenGanttChartArea', false));

  // マスタ、組織情報の取得
  const options = useDefaultOptions(language);
  const {
    data: mastersData,
    isLoading: mastersIsLoading,
    isError: mastersIsError,
    error: mastersError,
  } = useGetScheduleListMasters(options);

  // 初期検索条件（セッションストレージに保存されている）
  const [defaultConditions] = useState<ScheduleListSearchConditions>(
    sessionStrageUtils.get<ScheduleListSearchConditions>('ScheduleList.SearchConditions', {})
  );

  // 検索条件
  const refConditions = useRef(
    sessionStrageUtils.get<ScheduleListSearchConditions>('ScheduleList.SearchConditions', {})
  );

  // 検索 API 情報
  const {
    data: responseData,
    isLoading,
    isError,
    error,
    mutateAsync: searchAsync,
  } = usePostScheduleListSearch<AxiosError<ErrorInfo>, unknown>(options);

  // 条件を保存し、検索する
  const setConditionsAndSearch = useCallback(
    (next: ScheduleListSearchConditions) => {
      const postSchedules = async () => {
        // API アクセスが成功した場合
        const onSuccess = () => {
          sessionStrageUtils.set('ScheduleList.SearchConditions', next);
          refConditions.current = next;
          openGanttChartArea.current = true;
          sessionStrageUtils.set('ScheduleList.OpenGanttChartArea', true);
        };
        // API アクセスの実行
        await searchAsync({ data: next }, { onSuccess });
      };
      void postSchedules();
    },
    [searchAsync]
  );
  // 一覧の初回表示
  useEffect(() => {
    const postSchedules = async () => {
      await searchAsync({ data: defaultConditions });
    };
    if (openGanttChartArea.current) {
      void postSchedules();
    }
  }, [defaultConditions, openGanttChartArea, searchAsync]);

  // 子日程個別表示リスト
  const [showChildList, setShowChildList] = useState<string[]>([]);
  // 子日程表示切替関数
  const changeChildList = (targetProductionId: string) => {
    // すでに存在している場合、削除
    if (isShowChild(targetProductionId)) {
      setShowChildList(showChildList.filter((psVersionId) => psVersionId !== targetProductionId));
      // 存在しない場合、挿入
    } else {
      setShowChildList([...showChildList, targetProductionId]);
    }
  };
  // 子日程個別表示リストに存在するかどうか
  const isShowChild = (targetProductionId: string) => {
    const index = showChildList.findIndex((x) => x === targetProductionId);
    return index >= 0;
  };

  // 現在表示中のページ番号
  const [currentPage, setCurrentPage] = useState(sessionStrageUtils.get('ScheduleList.CurrentPage', 1));

  // ソート情報
  const [sortInfo, setSortInfo] = useState(
    sessionStrageUtils.get('ScheduleList.SortInfo', { key: ['preproduction'] as ScheduleSortKey[], isDisc: false })
  );

  // 初回ロード中かどうか
  const refIsFirstLoading = useRef(true);

  // 一覧データをソートしたもの
  const sortedScheduleList = useMemo(
    () =>
      [...(responseData?.data.schedule_list_items ?? [])].sort((a, b) => {
        const aAny = a as unknown as { [key: string]: unknown | undefined };
        const bAny = b as unknown as { [key: string]: unknown | undefined };
        const multiValue = sortInfo.isDisc ? -1 : 1;

        if (sortInfo.key.length === 1) {
          // 第一ソートキー
          return ((aAny[sortInfo.key[0]] ?? '') > (bAny[sortInfo.key[0]] ?? '') ? 1 : -1) * multiValue;
        }

        const aAnyObject = aAny[sortInfo.key[0]] as ScheduleListPeriod;
        const bAnyObject = bAny[sortInfo.key[0]] as ScheduleListPeriod;
        if (sortInfo.key[1] === 'plan') {
          // 第一ソートキー
          return ((aAnyObject.plan_from_on ?? '') > (bAnyObject.plan_from_on ?? '') ? 1 : -1) * multiValue;
        }
        if (sortInfo.key[1] === 'actual') {
          // 第一ソートキー
          return ((aAnyObject.actual_from_on ?? '') > (bAnyObject.actual_from_on ?? '') ? 1 : -1) * multiValue;
        }

        // デフォルトソートキー
        return ((a.preproduction.plan_from_on ?? '') > (b.preproduction.plan_from_on ?? '') ? 1 : -1) * multiValue;
      }),
    [sortInfo, responseData]
  );

  // 現在のページに表示する一覧
  const scheduleListForPresentPage = useMemo(
    () => sortedScheduleList.slice(N_DATA_PER_PAGE * (currentPage - 1), N_DATA_PER_PAGE * currentPage),
    [currentPage, sortedScheduleList]
  );

  // 一覧のソートキーを変更する関数
  const changeSortKey = useCallback((key: ScheduleSortKey[]) => {
    setSortInfo((pre) => {
      const next = { key, isDisc: JSON.stringify(pre.key) === JSON.stringify(key) && !pre.isDisc };
      sessionStrageUtils.set('ScheduleList.SortInfo', next);
      return next;
    });
    setCurrentPage(1);
  }, []);

  // 現在ページを変更する関数
  const changeCurrentPage = useCallback(
    (next: number) => {
      sessionStrageUtils.set('ScheduleList.CurrentPage', next);
      setCurrentPage(next);
    },
    [setCurrentPage]
  );

  // 日程一覧検索 エラーハンドリング用
  const [displayError, setDisplayError] = useState(false);

  // データが変更されたときの動作
  useEffect(() => {
    if (isError) {
      setDisplayError(true);
      return;
    }

    if (responseData?.data.schedule_list_items != null) {
      if (refIsFirstLoading.current) {
        // 初回ロード完了時は初回ロードが完了したことを設定する
        refIsFirstLoading.current = false;
      } else {
        // 初回ロード以外にデータが変更されたときは、現在ページを 1 ページにする
        setCurrentPage(1);
      }
    }
  }, [isError, responseData?.data.schedule_list_items]);

  // 日程一覧出力 API 準備
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: downloadAsync,
    isError: isDownloadError,
    error: downloadError,
  } = usePostScheduleListDownload<AxiosError<Blob>>(optionsForBinary);
  // 日程一覧出力 エラーハンドリング用
  const [displayDownloadError, setDisplayDownloadError] = useState(false);
  // 日程一覧出力判定用
  const [displaySpinnerModal, setDisplaySpinnerModal] = useState(false);
  // 日程一覧出力
  const download = useCallback(async () => {
    setDisplaySpinnerModal(true);
    const param: ScheduleListDownloadList = getScheduleListDownloadList(sortedScheduleList);
    const response = await downloadAsync({ data: param });
    setDisplaySpinnerModal(false);
    if (response != null) {
      executeDownload(response);
    } else if (isDownloadError) {
      setDisplayDownloadError(true);
    }
  }, [downloadAsync, isDownloadError, sortedScheduleList]);

  useEffect(() => {
    setDisplayDownloadError(isDownloadError);
  }, [isDownloadError]);

  // 待ち状態
  if (mastersIsLoading) {
    return (
      <div id="main">
        <Title />
        <WaitComponent />
      </div>
    );
  }

  // エラー
  if (mastersIsError) {
    return (
      <div id="main">
        <Title />
        {mastersIsError && <ErrorComponent error={mastersError} />}
      </div>
    );
  }

  // データが取得できない場合
  if (
    mastersData.data.model_names == null ||
    mastersData.data.destinations == null ||
    mastersData.data.production_divisions == null ||
    mastersData.data.model_users == null ||
    mastersData.data.languages == null ||
    mastersData.data.production_statuses == null ||
    mastersData.data.companies == null
  ) {
    return (
      <div id="main">
        <Title />
        <NoDataComponent />
      </div>
    );
  }
  return (
    <div id="main">
      <Title />
      <section>
        <h3 className="trig">
          <span>
            {t('日程検索')}
            {/* 検索条件の開閉 */}
            <button
              type="button"
              className="text-button"
              onClick={(e) => {
                e.preventDefault();
                setOpenSearchConditions((pre) => !pre);
              }}
            >
              <i className={`fa-solid ${openSearchConditions ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
            </button>
          </span>
        </h3>
        {/* 日程検索欄 */}
        <ScheduleListSerchCondition
          openSearchConditions={openSearchConditions}
          defaultConditions={defaultConditions}
          conditionsRef={refConditions}
          setConditionsAndSearch={setConditionsAndSearch}
          setShowChildList={setShowChildList}
          modelNames={mastersData.data.model_names}
          destinations={mastersData.data.destinations}
          productionDevisions={mastersData.data.production_divisions}
          treeCompanies={mastersData.data.model_users}
          statuses={mastersData.data.production_statuses}
          languages={mastersData.data.languages}
          companies={mastersData.data.companies}
        />

        <h3 className="nh3-1">
          <span>{t('ガントチャート')}</span>
        </h3>
        <GanttChartArea
          openGanttChart={openGanttChartArea.current}
          isShowChild={isShowChild}
          changeChildList={changeChildList}
          setShowChildList={setShowChildList}
          // ガントチャートデータ
          data={scheduleListForPresentPage}
          allData={responseData?.data.schedule_list_items ?? []}
          isLoading={isLoading}
          isError={isError}
        />

        <div className="master-navi gantt mt60 mb15">
          <h3 className="mb0">
            <span>{t('詳細表示')}</span>
          </h3>
          <div className="outputfr2">
            <button type="button" className="text-button schedule-export" onClick={download}>
              <i className="fa-solid fa-download" />
              {t('エクスポート')}
            </button>
          </div>
        </div>
        <ScheduleListIndexArea
          openGanttChart={openGanttChartArea.current}
          // ガントチャートデータ
          data={scheduleListForPresentPage}
          allDatalength={(responseData?.data.schedule_list_items ?? []).length}
          isLoading={isLoading}
          isError={isError}
          isShowChild={isShowChild}
          changeSortKey={changeSortKey}
          changeCurrentPage={changeCurrentPage}
          changeChildList={changeChildList}
          currentPage={currentPage}
        />
        {/* スピナーモーダル */}
        {displaySpinnerModal && <SpinnerModal />}
        {/* ダウンロードエラー */}
        {displayDownloadError && (
          <BlobErrorModal
            error={downloadError}
            onClickOk={() => {
              setDisplayDownloadError(false);
              setDisplaySpinnerModal(false);
            }}
          />
        )}
        {/* 検索結果がエラーだった場合 */}
        {displayError && (
          <ErrorModal
            onClickOk={() => {
              setDisplayError(false);
            }}
            error={error}
          />
        )}
      </section>
    </div>
  );
}

/**
 * ダウンロード用パラメータ作成
 * @param sortedScheduleList 並び替え済み検索結果
 */
function getScheduleListDownloadList(sortedScheduleList: ScheduleListItem[]):ScheduleListDownloadList{
  const scheduleListDownloadList: ScheduleListDownloadListItem[] = [];
  sortedScheduleList.forEach((schedule) => {
    const scheduleListDownload: ScheduleListDownloadListItem = {
      production_id: schedule.production_id,
    };
    if (schedule.children !== undefined && schedule.children.length > 0) {
      scheduleListDownload.children = [];
      schedule.children.forEach((child) => {
        const childItem: ScheduleListDownloadListItem = {
          production_id: child.production_id,
        };
        scheduleListDownload.children?.push(childItem);
      });
    }
    scheduleListDownloadList.push(scheduleListDownload);
  });
  const param:ScheduleListDownloadList = {
    schedule_list_download_list_items:scheduleListDownloadList
  };
  return param;
};
export default ScheduleListPage;

